import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminApiPricingQueryKey,
  useDeleteSuperAdminApiPricingId,
  useGetSuperAdminApiPricing,
  useGetSuperAdminFranchises,
  useGetSuperAdminFranchisesFranchiseIdUsage,
  useGetSuperAdminUsagePlatform,
  usePatchSuperAdminApiPricingId,
  usePostSuperAdminApiPricing,
} from '@agos/api-client/platform'
import {
  T,
  currentMonthInput,
  formatCount,
  monthWindow,
  reportWindowParams,
  tt,
  useModalFocus,
} from '@agos/shared'
import { useQueryClient } from '@tanstack/react-query'
import { TranslationFailuresPanel } from './TranslationFailuresPanel'
import { useMemo, useRef, useState } from 'react'
import { PageHeader, FilterSelect, ReadFailed } from '@agos/ui'

import {
  BILLING_MODES,
  emptyWindowNote,
  fieldsFor,
  isUndercounted,
  modeNote,
  percentRuleBillsNothing,
  percentRuleWarning,
  platformRevenueLabel,
  platformUsageOf,
  platformWindowNote,
  pricingRulesOf,
  operationUsageOf,
  operationsFor,
  rateProblem,
  rateProblemMessage,
  reconcile,
  failureSummary,
  outcomeNote,
  reconciliationNote,
  rulesWithoutUsage,
  servicesWithoutRevenue,
  serviceUsageOf,
  undercountNote,
  unpricedCalls,
  zeroCostNote,
  type BillingMode,
  type PricingRule,
} from './api-pricing'

/**
 * What is metered against what is priced - `SAAS.md` §3.10.
 *
 * **FB-505:** *"the cost side of `Profitability` is read-only, so the margins it reports cannot be
 * corrected."* The finding behind this screen is not the missing form: it is that **metered and
 * priced are two different lists and nothing shows the gap.**
 *
 * `ProfitabilityService.unitCost`'s javadoc claims an unpriced service reads as *"pure profit, and
 * visibly so"*. It produces no line at all. On the owner's data that is 12 368 of franchise 1's
 * 111 251 calls - and a 2 % card fee configured on the one franchise with no card volume.
 */

/*
 * The window and the month this page opens on both come from `@agos/shared` - FB-964, `SAAS.md`
 * §3.188. They were nine lines here, built with `Date.UTC(...)`, and that frame is the whole defect:
 * `/franchises/{id}/usage` answered **101** calls for franchise 1 in September to this screen and
 * **97** to `FranchiseUsagePage`, which sends the shared local boundaries. Same endpoint, same month,
 * two figures, nothing on either screen saying why.
 */

type MoneyField =
  | 'priceCentsPerUnit'
  | 'costCentsPerUnit'
  | 'monthlyFlat'
  | 'monthlyFlatCost'
  | 'freeAllowance'
  | 'percentRate'

/**
 * Each box's label, each inside its own `tt()` call and looked up at render.
 *
 * A function rather than a module-level map, for `TowerShell.navItems`'s reason: a `tt()` evaluated
 * at import runs before a locale is chosen, and the extractor needs to see every literal.
 */
function fieldLabel(field: MoneyField): string {
  if (field === 'priceCentsPerUnit') return tt('Cena / volanie (c)')
  if (field === 'costCentsPerUnit') return tt('Náklad / volanie (c)')
  if (field === 'monthlyFlat') return tt('Paušál (€)')
  if (field === 'monthlyFlatCost') return tt('Náklad paušálu (€)')
  if (field === 'freeAllowance') return tt('Voľné volania')
  return tt('Podiel z objemu (%)')
}

export function ApiPricingPage() {
  const rulesQuery = useGetSuperAdminApiPricing()
  const franchiseList = useGetSuperAdminFranchises()
  const rules = useMemo(() => pricingRulesOf(rulesQuery.data), [rulesQuery.data])

  const franchises = useMemo(() => {
    const rows = Array.isArray(
      (franchiseList.data as { franchises?: unknown } | undefined)?.franchises,
    )
      ? (franchiseList.data as { franchises: Record<string, unknown>[] }).franchises
      : []
    return rows.flatMap((row) =>
      typeof row.id === 'number'
        ? [{ id: row.id, name: typeof row.name === 'string' ? row.name : `#${row.id}` }]
        : [],
    )
  }, [franchiseList.data])

  const [chosen, setChosen] = useState<number | null>(null)
  const franchiseId = chosen ?? franchises[0]?.id ?? null
  const [month, setMonth] = useState(() => currentMonthInput())
  // Not named `window`: it would shadow the global, which is harmless today and a trap the
  // moment anything on this page reads `window.location`.
  //
  // Through `reportWindowParams` for its own documented reason: a month `monthWindow` cannot read
  // answers two blanks, and `from=` reaches the server's parser as an unparseable value where an
  // absent `from` picks its default. So the three screens that take a date range and this one that
  // takes a month now send their bounds the same way.
  const period = useMemo(() => reportWindowParams(monthWindow(month)), [month])

  const usageQuery = useGetSuperAdminFranchisesFranchiseIdUsage(franchiseId ?? 0, period, {
    query: { enabled: franchiseId !== null },
  })
  /**
   * The same period, across every franchise - §3.76.
   *
   * **This endpoint had no caller of any kind until now.** Its own controller summarises it as *"The
   * same, across every franchise"*, and this screen read only the per-franchise one - so a
   * reconciliation screen could be read one tenant at a time while owner questions #19, #20 and #21
   * were all cross-franchise ones. Not gated on `franchiseId`: it is the answer for *all* of them,
   * and gating it on a picker it does not use is how a panel comes to look broken on an empty
   * tenant.
   */
  const platformQuery = useGetSuperAdminUsagePlatform(period)
  const platform = useMemo(() => platformUsageOf(platformQuery.data), [platformQuery.data])
  const unpricedAcrossThePlatform = useMemo(
    () => servicesWithoutRevenue(platform.byService),
    [platform.byService],
  )
  const usage = useMemo(() => serviceUsageOf(usageQuery.data), [usageQuery.data])
  // §3.105 - out of the **same** response as `usage` above, which already groups by operation. No
  // extra request, so the operation box can offer real values on a page that was already loaded.
  const operations = useMemo(() => operationUsageOf(usageQuery.data), [usageQuery.data])
  // §3.107 - the two nothing-states below are about the **usage** reads, so each is gated on its
  // own. Folding them would say *no metered calls* when it was the rule list that failed, which is
  // the wrong reason rather than a slow screen.
  const usageFailed = usageQuery.isError
  const platformFailed = platformQuery.isError
  const rulesFailed = rulesQuery.isError
  const rows = useMemo(
    () => (franchiseId === null ? [] : reconcile(usage, rules, franchiseId)),
    [usage, rules, franchiseId],
  )
  const idle = useMemo(
    () => (franchiseId === null ? [] : rulesWithoutUsage(rules, usage, franchiseId)),
    [rules, usage, franchiseId],
  )

  const queryClient = useQueryClient()
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetSuperAdminApiPricingQueryKey() })
  /**
   * What the server refused, said out loud - `SAAS.md` §3.35.
   *
   * **Three mutations, no `onError`.** A rule sets the price of a metered API call, and §3.10 found a
   * negative one becoming a negative line on an issued invoice - so the server refuses, and the
   * refusal had nowhere to appear.
   *
   * `apiErrorMessage` is the shared reader - it takes `error` off the backend's `{error, code}`
   * envelope. The fallback covers a network drop, which carries no envelope to read.
   */
  const [refused, setRefused] = useState<string | null>(null)
  const refusedWith = (error: unknown) => {
    setRefused(apiErrorMessage(error, tt('Zmenu sa nepodarilo uložiť - server neodpovedal.')))
  }

  // A refusal is cleared the moment anything succeeds - a banner left over from an earlier attempt
  // is a screen stating something that is no longer true.
  const succeeded = () => {
    setRefused(null)
    refresh()
  }

  const createRule = usePostSuperAdminApiPricing({ mutation: { onSuccess: succeeded, onError: refusedWith } })
  const patchRule = usePatchSuperAdminApiPricingId({ mutation: { onSuccess: succeeded, onError: refusedWith } })
  const deleteRule = useDeleteSuperAdminApiPricingId({ mutation: { onSuccess: succeeded, onError: refusedWith } })

  // The rule being edited, and the raw text of each money box - raw because a comma is what a
  // Slovak keyboard types and `Number('2,50')` is NaN.
  const [editing, setEditing] = useState<{
    id: number | null
    service: string
    billingMode: BillingMode
    rates: Partial<Record<MoneyField, string>>
    forFranchise: boolean
    // §3.105 - four fields `PricingRule` has always parsed and this page drew none of.
    operation: string
    priceIncludesVat: boolean
    enabled: boolean
    description: string
  } | null>(null)
  const [asking, setAsking] = useState<PricingRule | null>(null)

  /* §9.489, `TODO.md` 3f - this confirmation said `aria-modal="true"` and confined nothing. It is the shape
     the trap was written for: a destructive question whose *Zmazať* must not take the opening focus.
     Guarded on the mutation, because dismissing it mid-delete is the one moment Escape must do nothing. */
  const confirmRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: confirmKeyDown } = useModalFocus(
    asking !== null,
    confirmRef,
    deleteRule.isPending ? null : () => setAsking(null),
  )

  const openFor = (rule: PricingRule | null, service: string) =>
    setEditing({
      id: rule?.id ?? null,
      service: rule?.service ?? service,
      billingMode: rule?.billingMode ?? 'per_unit',
      rates: rule
        ? {
            priceCentsPerUnit: String(rule.priceCentsPerUnit),
            costCentsPerUnit: String(rule.costCentsPerUnit),
            monthlyFlat: String(rule.monthlyFlat),
            monthlyFlatCost: String(rule.monthlyFlatCost),
            freeAllowance: String(rule.freeAllowance),
            percentRate: String(rule.percentRate),
          }
        : {},
      // A new rule for the chosen franchise by default rather than platform-wide: the reconciliation
      // is per franchise, and a catch-all typed from a franchise's row is how a rate meant for one
      // restaurant reaches all of them.
      forFranchise: rule === null ? true : rule.franchiseId !== null,
      // §3.105. `null` is *any operation within the service* and an empty box says the same thing,
      // so they map onto each other without a third state.
      operation: rule?.operation ?? '',
      priceIncludesVat: rule?.priceIncludesVat ?? false,
      // A new rule is on. That is what `ApiPricingService.create` defaults to and what somebody
      // typing a rate means; an off-by-default rule would be typed, saved and silently ignored.
      enabled: rule?.enabled ?? true,
      description: rule?.description ?? '',
    })

  const shown = editing === null ? [] : (fieldsFor(editing.billingMode) as MoneyField[])
  const problems = shown
    .map((field) => ({ field, problem: rateProblem(editing?.rates[field] ?? '0', field) }))
    .filter((entry) => entry.problem !== null)

  function bodyOf(draft: NonNullable<typeof editing>): Record<string, unknown> {
    const body: Record<string, unknown> = {
      service: draft.service.trim(),
      billingMode: draft.billingMode,
    }
    if (draft.id === null) {
      body.franchiseId = draft.forFranchise ? franchiseId : null
    }
    for (const field of fieldsFor(draft.billingMode) as MoneyField[]) {
      body[field] = Number((draft.rates[field] ?? '0').trim().replace(',', '.'))
    }
    // §3.105. **An empty box is `null`, not `''`** - the matcher compares `operation` with
    // `Objects.equals`, so a rule storing the empty string would match calls whose operation is
    // also empty rather than being the *any operation* rule the blank box means. Same for the
    // description, where `''` and *nothing written* are not two states worth keeping apart.
    body.operation = draft.operation.trim() === '' ? null : draft.operation.trim()
    body.description = draft.description.trim() === '' ? null : draft.description.trim()
    // Real booleans: `validate` does not coerce these and the column is `boolean NOT NULL`.
    body.priceIncludesVat = draft.priceIncludesVat
    body.enabled = draft.enabled
    return body
  }

  return (
    <main className="space-y-3" data-api-pricing>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      <PageHeader title={tt('Ceny API')} description={tt('Merané volania proti pravidlám, ktoré ich oceňujú.')} />

      {refused !== null && (
        <p
          className="rounded-2xl border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-500"
          data-api-pricing-refused
          role="alert"
        >
          {refused}
        </p>
      )}
      {/* ─── The window and the franchise, which decide everything below ───── */}
      <section className="rounded-2xl border border-border p-3 space-y-2" data-reconciliation>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <T>Merané verzus ocenené</T>
          </p>
          {/* §3.117 - a filter rather than a form field: it decides whose metered-versus-priced
            * reconciliation the panel shows. The `aria-label` is what it had none of. */}
          <FilterSelect
            className="min-h-[32px] text-xs"
            value={franchiseId === null ? '' : String(franchiseId)}
            onChange={(chosen) => setChosen(Number(chosen))}
            label={tt('Prevádzka')}
            testId="data-franchise-picker"
            options={franchises.map((franchise) => ({
              value: String(franchise.id),
              label: franchise.name,
            }))}
          />
          {/* The window is a control rather than a default, because the default is thirty days back
            * and the restored data ends in July - so the most reassuring state of this screen would
            * otherwise be its least informative one. */}
          <input
            type="month"
            data-window-month
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
          />
        </div>

        {/* §3.107 - `emptyWindowNote` reads *nothing in this period*, which a refused read cannot
          * claim. The rule list is named separately because the note's own wording depends on it. */}
        {(usageFailed || rulesFailed) && (
          <ReadFailed marker="pricing-error">{usageFailed ? (
              <T>Spotrebu tejto franšízy sa nepodarilo načítať.</T>
            ) : (
              <T>Cenové pravidlá sa nepodarilo načítať.</T>
            )}</ReadFailed>
        )}
        {usageFailed || rulesFailed ? null : emptyWindowNote(usage, rules.length > 0) !== '' ? (
          <p className="text-xs text-amber-500" data-empty-window>
            {emptyWindowNote(usage, rules.length > 0)}
          </p>
        ) : (
          <p
            className={`text-xs ${unpricedCalls(rows) > 0 ? 'font-semibold text-amber-500' : 'text-muted-foreground'}`}
            data-reconciliation-note
            data-unpriced-calls={String(unpricedCalls(rows))}
          >
            {reconciliationNote(rows)}
          </p>
        )}
      </section>

      {/* ─── Why this franchise's translations fail - §3.89 ─────────────────── */}
      {franchiseId !== null && <TranslationFailuresPanel franchiseId={franchiseId} />}

      {/* ─── The whole platform, which is the question the owner keeps asking - §3.76 ────── */}
      <section className="rounded-2xl border border-border p-3 space-y-2" data-platform-usage>
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <T>Celá platforma</T>
          </p>
          <span className="text-[11px] text-muted-foreground" data-platform-window>
            {platformWindowNote(platform)}
          </span>
        </div>

        {platformFailed && (
          <ReadFailed marker="platform-error"><T>Platformovú spotrebu sa nepodarilo načítať.</T></ReadFailed>
        )}
        {platformFailed ? null : platform.byService.length === 0 ? (
          /* An absence said rather than drawn as an empty table - §9.410's distinction. The window
           * is a control on this screen and the restored data ends in July, so *nothing logged* is
           * the ordinary state of a recent month rather than a fault. */
          <p className="text-xs text-muted-foreground" data-platform-empty>
            <T>V tomto období nie sú zaznamenané žiadne merané volania.</T>
          </p>
        ) : (
          <>
            {unpricedAcrossThePlatform.length > 0 && (
              /* **The finding, above the figures.** A service with calls and no amount can never
               * produce a line however a rule is configured - §3.43 - so this is the sentence that
               * answers #19 and #21 rather than the table below it. */
              <p
                className="text-xs font-semibold text-amber-500"
                data-platform-unpriced={String(unpricedAcrossThePlatform.length)}
              >
                {unpricedAcrossThePlatform.map((row) => row.service).join(', ')}{' '}
                <T>nezaznamenávajú sumu, z ktorej by sa dalo počítať percento.</T>
              </p>
            )}

            {undercountNote(platform.byService) !== '' && (
              /* §3.77 - said once, under the rows that carry the marker. **A figure the ledger
               * already records as low must not be offered as the thing to decide against**: §3.76
               * pointed owner questions #19, #20 and #21 at this panel, and #21 is *what should a
               * Google call cost*. */
              <p className="text-[11px] text-amber-500" data-platform-undercount-note>
                {undercountNote(platform.byService)}
              </p>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <T>Podľa služby</T>
                </p>
                {platform.byService.map((row) => (
                  <div
                    key={row.service}
                    className="flex items-baseline justify-between gap-2 rounded-xl border border-border p-2 text-xs"
                    data-platform-service={row.service}
                  >
                    <span className="font-mono">
                      {row.service}
                      {isUndercounted(row.service) && (
                        /* §3.77 - a marker rather than a repeated sentence; the note below says what
                         * it means. This figure counted one of two Google clients until the order
                         * path was metered, and #21 is a per-call price question. */
                        <span className="ml-1 text-amber-500" data-platform-undercounted={row.service}>
                          *
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground">{formatCount(row.calls)}</span>
                    <span
                      className={row.revenueCents === 0 && row.calls > 0 ? 'text-amber-500' : ''}
                      data-platform-service-revenue
                    >
                      {platformRevenueLabel(row)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <T>Podľa franšízy</T>
                </p>
                {platform.byFranchise.map((row) => (
                  <div
                    key={row.franchiseId}
                    className="flex items-baseline justify-between gap-2 rounded-xl border border-border p-2 text-xs"
                    data-platform-franchise={String(row.franchiseId)}
                  >
                    <span>{row.franchiseName}</span>
                    <span className="text-muted-foreground">{formatCount(row.calls)}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      {/* ─── Every metered service, and what prices it ──────────────────────── */}
      <section className="space-y-1" data-service-list>
        {/* Worst share first, not biggest count - §3.89. 104 failures out of 4 602 is an ordinary
          * afternoon; 9 out of 9 is an integration that has never worked, and ordering by count
          * hides the second behind the first. */}
        {failureSummary(rows) !== '' && (
          <p className="text-xs font-semibold text-amber-500" data-failing-services>
            {failureSummary(rows)}
          </p>
        )}
        {rows.map((row) => (
          <div
            key={row.service}
            className={`rounded-xl border p-2 text-xs space-y-1 ${row.unpriced ? 'border-amber-500/40' : 'border-border'}`}
            data-service={row.service}
            data-service-unpriced={row.unpriced ? 'yes' : 'no'}
            data-service-disabled={row.disabled ? 'yes' : 'no'}
          >
            <p>
              <span className="font-semibold">{row.service}</span>
              <span className="text-muted-foreground">
                {' '}
                &middot; {formatCount(row.calls)} <T>volaní</T>
              </span>
              {/* §3.89. The server has answered `success` and `failure` on this grouping the whole
                * time; `serviceUsageOf` parsed the first and read neither. A call count rises just
                * as fast when every call fails. */}
              {outcomeNote(row) !== '' && (
                <span
                  className={row.failure > 0 ? 'text-amber-500' : 'text-muted-foreground'}
                  data-service-outcome={row.service}
                  data-service-failures={String(row.failure)}
                >
                  {' '}
                  &middot; {outcomeNote(row)}
                </span>
              )}
            </p>
            {row.unpriced ? (
              <p className="text-[10px] text-amber-500" data-service-no-rule>
                <T>Žiadne pravidlo - nefakturuje sa ani nevykazuje ako náklad.</T>
              </p>
            ) : (
              <p className="text-[10px] text-muted-foreground" data-service-rule>
                {modeNote(row.rule!.billingMode)}
                {/* §3.105 - the rule's own note, which 3 of the owner's 6 rules carry and no screen
                  * showed. It is the only place a rule can say *why* it is what it is, and the list
                  * is where somebody reads it: opening the form to find out requires knowing there
                  * is something to find. */}
                {row.rule!.description !== null && (
                  <span data-service-rule-note>
                    {' '}
                    &middot; {row.rule!.description}
                  </span>
                )}
                {/* And which operation it is scoped to, when it is scoped to one. A rule reading
                  * *per_unit* beside a service that also has a broader rule says nothing about
                  * which calls it actually prices. */}
                {row.rule!.operation !== null && (
                  <span data-service-rule-operation>
                    {' '}
                    &middot; {row.rule!.operation}
                  </span>
                )}
                {row.rule!.priceIncludesVat && (
                  <span data-service-rule-gross>
                    {' '}
                    &middot; <T>s DPH</T>
                  </span>
                )}
                {row.disabled && (
                  <span className="text-amber-500" data-service-rule-off>
                    {' '}
                    - <T>pravidlo je vypnuté</T>
                  </span>
                )}
              </p>
            )}
            {/* §3.43 - a percent rule on a service that logs no amount. `BillingRunner` takes the
              * percentage of `SUM(metadata->>'amountCents')`, and measured across the owner's
              * 112 250 calls **only `tatrapay` carries one** - so such a rule is enabled, configured
              * and worth nought for ever, with nothing anywhere saying why.
              *
              * Separate from *Pravidlá bez merania* above on purpose: that one is a rule no call
              * landed on, which moving the rule to another franchise would fix. This one no
              * franchise can fix. */}
            {row.rule !== null && percentRuleBillsNothing(row.rule, usage) && (
              <p className="text-[10px] font-semibold text-red-500" data-service-percent-void>
                {percentRuleWarning(row.rule)}
              </p>
            )}
            {zeroCostNote(row) !== '' && (
              <p className="text-[10px] text-amber-500" data-service-zero-cost>
                {zeroCostNote(row)}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                data-service-edit={row.service}
                onClick={() => openFor(row.rule, row.service)}
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
              >
                {row.unpriced ? <T>Oceniť</T> : <T>Zmeniť pravidlo</T>}
              </button>
              {row.rule !== null && (
                <button
                  type="button"
                  data-service-delete={row.service}
                  onClick={() => setAsking(row.rule)}
                  className="px-2 py-1 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-bold"
                >
                  <T>Zmazať pravidlo</T>
                </button>
              )}
            </div>
          </div>
        ))}
      </section>

      {/* ─── A rule this franchise has and nothing matched ──────────────────── */}
      {idle.length > 0 && (
        <section className="rounded-2xl border border-amber-500/40 p-3 space-y-1" data-idle-rules>
          <p className="text-[11px] font-bold uppercase tracking-wider text-amber-500">
            <T>Pravidlá bez merania</T>
          </p>
          {/* The `tatrapay` case: a 2 % card fee on a franchise with no card volume. It bills
            * nothing and looks configured, and nothing else would ever have surfaced it. */}
          <p className="text-[10px] text-muted-foreground">
            <T>Pravidlo pre túto prevádzku, na ktoré v tomto období nepadlo ani jedno volanie.</T>
          </p>
          {idle.map((rule) => (
            <p key={rule.id} className="text-xs" data-idle-rule={rule.service}>
              <span className="font-semibold">{rule.service}</span> &middot;{' '}
              {modeNote(rule.billingMode)}
            </p>
          ))}
        </section>
      )}

      {/* ─── The form ───────────────────────────────────────────────────────── */}
      {editing !== null && (
        <section className="rounded-2xl border border-border p-3 space-y-2" data-rule-form>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {editing.id === null ? <T>Nové pravidlo</T> : <T>Pravidlo</T>} &middot; {editing.service}
          </p>
          <select
            data-rule-mode
            value={editing.billingMode}
            onChange={(event) =>
              setEditing({ ...editing, billingMode: event.target.value as BillingMode })
            }
            className="min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
          >
            {BILLING_MODES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          {/* What the mode bills, said rather than inferred from which box is filled. */}
          <p className="text-[10px] text-muted-foreground" data-rule-mode-note>
            {modeNote(editing.billingMode)}
          </p>

          {/* Only the boxes this mode reads. The rest are not filled with noughts, because a nought
            * in a box the mode ignores reads as a decision somebody made. */}
          <div className="flex flex-wrap gap-1.5">
            {shown.map((field) => (
              <label key={field} className="text-[10px]">
                <span className="text-muted-foreground">{fieldLabel(field)}</span>
                <input
                  data-rate-field={field}
                  inputMode="decimal"
                  value={editing.rates[field] ?? ''}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      rates: { ...editing.rates, [field]: event.target.value },
                    })
                  }
                  className="block w-28 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
                />
              </label>
            ))}
          </div>
          {problems.map((entry) => (
            <p
              key={entry.field}
              className="text-[10px] text-red-500"
              data-rate-problem={entry.field}
            >
              {fieldLabel(entry.field)}: {rateProblemMessage(entry.problem)}
            </p>
          ))}

          {/* §3.105 - the four fields this page's own parser has always read and nothing drew.
            * Below the rates on purpose: the rate is what somebody opened the form to type, and
            * these qualify it. */}
          <label className="text-[10px] block">
            <span className="text-muted-foreground">
              <T>Operácia (prázdne = všetky)</T>
            </span>
            <input
              data-rule-operation
              list="rule-operations"
              value={editing.operation}
              onChange={(event) => setEditing({ ...editing, operation: event.target.value })}
              className="block w-44 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
            {/* What the franchise has actually called for this service, off the `byOperation` the
              * usage payload already carries. A mistyped operation matches nothing for ever, and
              * the only place that shows up is a billing run weeks later. */}
            <datalist id="rule-operations">
              {operationsFor(operations, editing.service).map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </label>
          <label className="text-[10px] block">
            <span className="text-muted-foreground">
              <T>Poznámka</T>
            </span>
            <input
              data-rule-description
              value={editing.description}
              onChange={(event) => setEditing({ ...editing, description: event.target.value })}
              className="block w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
          </label>
          <label className="flex items-center gap-1.5 text-[10px]">
            <input
              type="checkbox"
              data-rule-includes-vat
              checked={editing.priceIncludesVat}
              onChange={(event) =>
                setEditing({ ...editing, priceIncludesVat: event.target.checked })
              }
            />
            {/* A statement about the figure that was typed, not a rate. `BillingRunner.net` divides
              * a gross-quoted price down because the invoice adds VAT at the end, and the rate it
              * divides by comes from the invoice. */}
            <span>
              <T>Zadaná cena je s DPH</T>
            </span>
          </label>
          <label className="flex items-center gap-1.5 text-[10px]">
            <input
              type="checkbox"
              data-rule-enabled
              checked={editing.enabled}
              onChange={(event) => setEditing({ ...editing, enabled: event.target.checked })}
            />
            <span>
              <T>Pravidlo je aktívne</T>
            </span>
          </label>
          {/* What off *means*, which is the half an operator cannot guess and the half I got wrong
            * in a draft of this screen. `match` ignores `enabled` deliberately, so a disabled
            * franchise rule **still shadows** the platform-wide one under it: the service stops
            * being charged for this franchise rather than reverting to the standard rate. That is
            * `matchingRule`'s own documented contract and `BACKEND.md` §10A.203; whether it is the
            * behaviour the owner wants is question #37, and this sentence changes with the answer. */}
          {!editing.enabled && (
            <p className="text-[10px] text-muted-foreground" data-rule-disabled-note>
              <T>
                Vypnuté pravidlo neoceňuje nič - služba sa tejto prevádzke prestane účtovať.
                Neprepne sa na platformové pravidlo.
              </T>
            </p>
          )}

          {editing.id === null && (
            <label className="flex items-center gap-1.5 text-[10px]">
              <input
                type="checkbox"
                data-rule-for-franchise
                checked={editing.forFranchise}
                onChange={(event) =>
                  setEditing({ ...editing, forFranchise: event.target.checked })
                }
              />
              <span>
                <T>Len pre túto prevádzku</T>
              </span>
            </label>
          )}

          <div className="flex gap-1.5">
            <button
              type="button"
              data-rule-save
              disabled={
                problems.length > 0 ||
                editing.service.trim() === '' ||
                createRule.isPending ||
                patchRule.isPending
              }
              onClick={() => {
                const body = bodyOf(editing)
                if (editing.id === null) createRule.mutate({ data: body })
                else patchRule.mutate({ id: editing.id, data: body })
                setEditing(null)
              }}
              className="px-3 py-1.5 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
            >
              <T>Uložiť</T>
            </button>
            <button
              type="button"
              data-rule-cancel
              onClick={() => setEditing(null)}
              className="px-3 py-1.5 rounded-xl border border-border text-xs font-semibold"
            >
              <T>Zrušiť</T>
            </button>
          </div>
        </section>
      )}

      {asking !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={tt('Zmazať pravidlo')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-rule-confirm
        >
          <div
            ref={confirmRef}
            onKeyDown={confirmKeyDown}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-4 space-y-3"
          >
            {/* Says what deleting does rather than only asking: without a rule the service is
              * neither billed nor costed, which is the state this whole screen exists to surface. */}
            <p className="text-sm" data-rule-confirm-text>
              {`${tt('Zmazať pravidlo pre')} ${asking.service}? ${tt('Služba potom nebude fakturovaná ani vykazovaná ako náklad - presne to, čo táto obrazovka hľadá.')}`}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-rule-confirm-cancel
                data-dialog-cancel
                onClick={() => setAsking(null)}
                className="flex-1 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-rule-confirm-go
                disabled={deleteRule.isPending}
                onClick={() => {
                  deleteRule.mutate({ id: asking.id })
                  setAsking(null)
                }}
                className="flex-1 min-h-[36px] rounded-xl border border-red-500/40 text-red-500 text-xs font-bold disabled:opacity-40"
              >
                <T>Zmazať</T>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
